/**
 * The player.
 *
 * Two things here are load-bearing:
 *
 *  - **The engine is rebuilt only on `generation`.** A renewal keeps the same token and
 *    the same URL, so it must *not* touch the media element — that is what makes a long
 *    session seamless. Only a re-mint changes `generation`.
 *
 *  - **VOD position is restored after a re-mint.** `resumeAt` is captured before the
 *    switch, because afterwards the element has been reloaded and reads zero.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { CatalogItemDto } from '@filmodasi/shared';
import { useApi } from '../api/hooks';
import { api } from '../api/client';
import { useSession } from '../store/session';
import { useStreamSession } from '../player/useStreamSession';
import { useWatchProgress } from '../player/useWatchProgress';
import { attachEngine, type AttachedEngine, type EngineTracks } from '../player/engines';
import { PlayerControls } from '../player/PlayerControls';
import { ErrorBox, Spinner } from '../components/ui';
import { FavoriteButton } from '../components/FavoriteButton';
import { RecommendButton } from '../components/RecommendButton';
import { PlayOnTvButton } from '../components/PlayOnTvButton';
import { hasAvplay, useDeviceProfile } from '../device/useDeviceProfile';
import { useTvPlayerKeys } from '../layouts/tv/useTvPlayerKeys';
import { TvPlayerBar } from '../layouts/tv/TvPlayerBar';
import { useBackHandler } from '../layouts/tv/useBackStack';
import { TrackMenu } from '../player/TrackMenu';
import { useRealtimeChannel } from '../realtime/RealtimeContext';
import { useTvPlayback } from '../realtime/useTvPlayback';

/** Per-device A/V sync trim. See PlayerControls for why it is adjustable. */
const AUDIO_OFFSET_KEY = 'filmodasi.audioOffsetMs';

/**
 * MIME types for containers the server hands over as raw bytes.
 *
 * Asked of the actual browser rather than assumed: Chromium demuxes Matroska, so Chrome
 * and Edge play a .mkv holding H.264 + AAC, while Firefox and Safari cannot. A server
 * that refused these outright would black out most of a real VOD catalog for users whose
 * browser was perfectly capable.
 */
const CONTAINER_MIME: Record<string, string> = {
  mkv: 'video/x-matroska; codecs="avc1.42E01E, mp4a.40.2"',
  avi: 'video/x-msvideo',
  flv: 'video/x-flv',
  wmv: 'video/x-ms-wmv',
  mpg: 'video/mpeg',
  mpeg: 'video/mpeg',
};

/** '' = no, 'maybe'/'probably' = worth trying. */
function containerSupport(ext: string | null): 'yes' | 'unknown' | 'no' {
  if (!ext) return 'yes';
  const mime = CONTAINER_MIME[ext.toLowerCase()];
  if (!mime) return 'yes';
  const verdict = document.createElement('video').canPlayType(mime);
  if (verdict === 'probably') return 'yes';
  return verdict === 'maybe' ? 'unknown' : 'no';
}

export function WatchPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Only for the language preferences below. Everything else about the profile is enforced
  // server-side; this copy only decides which audio track to ask the engine for.
  const { profile } = useSession();
  /**
   * "Play from the beginning", set by the detail screen's second button.
   *
   * Read from the URL rather than passed in router state so that it survives a reload and
   * cannot be lost between the two screens — and so the intent is visible in the address
   * while debugging on the TV, where there is no console to ask.
   */
  const [search] = useSearchParams();
  const restart = search.get('restart') === '1';
  const engineRef = useRef<AttachedEngine | null>(null);
  const [playerError, setPlayerError] = useState<string | null>(null);
  /**
   * The attached engine's track interface, mirrored into state.
   *
   * A ref alone would not do: the engine attaches asynchronously, after the control bar
   * has rendered, and nothing would tell the bar its menu had become answerable. Null
   * means either "not attached yet" or "this engine cannot enumerate", and the menu says
   * the second — which is the truth a moment later either way.
   */
  const [tracks, setTracks] = useState<EngineTracks | null>(null);

  /**
   * The media element and its wrapper, held in state rather than a plain ref.
   *
   * A ref does not re-render when it is populated, so the control bar rendered on the
   * first pass would have been handed `null` and never heard about the element arriving —
   * it would sit there permanently showing 0:00.
   */
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const [stageEl, setStageEl] = useState<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  videoRef.current = videoEl;

  const item = useApi<CatalogItemDto>(id ? `/api/catalog/item/${id}` : null);
  const isLive = item.data?.type === 'live';
  const support = containerSupport(item.data?.containerExt ?? null);

  /**
   * Where ffmpeg was told to start, for the remux engine.
   *
   * The remuxed response is a pipe, so `video.currentTime` counts from the start of *this*
   * request, not from the start of the film. Everything user-facing adds this offset back.
   */
  const [remuxOffset, setRemuxOffset] = useState(0);

  /**
   * A/V sync trim, in milliseconds, remembered across films and reloads.
   *
   * Per browser rather than per account: the correction compensates for how *this*
   * browser handles the container's start timestamps, so it is a property of the device,
   * not of the viewer or the file.
   */
  const [audioOffsetMs, setAudioOffsetMs] = useState(() => Number(localStorage.getItem(AUDIO_OFFSET_KEY) ?? 0) || 0);

  /**
   * Where playback is, in seconds from the start of the film.
   *
   * Asks the engine first. AVPlay decodes on a hardware plane the DOM cannot see, so the
   * `<video>` element it sits over reports 0 forever — and every consumer of this
   * function (resume, Continue Watching, the ten-second progress report) would quietly
   * record zero on the one device where a film is most likely to be finished.
   *
   * The `remuxOffset` is always added for the element path: it stays 0 for every engine
   * but `remux`, which keeps this free of a circular dependency on a session that does
   * not exist yet.
   */
  const currentTime = useCallback(() => {
    const controls = engineRef.current?.controls;
    if (controls) return controls.currentTime();
    return (videoRef.current?.currentTime ?? 0) + remuxOffset;
  }, [remuxOffset]);
  const stream = useStreamSession(item.data ? id! : null, { isLive: Boolean(isLive), currentTime });
  const isRemux = stream.session?.engine === 'remux';

  /**
   * How long the film is, according to whoever knows best.
   *
   * The element read it from the file it is actually playing, so it wins — the provider's
   * metadata is frequently wrong or missing. Except on the remux engine, where the element
   * is reading a pipe and reports `Infinity`.
   *
   * One definition, three callers: the progress reporter, the phone remote's scrubber, and
   * the seek clamp on the remote. They were three copies of the same five lines, which is
   * three places for the remux exception to be forgotten in.
   */
  const playbackDuration = useCallback((): number | null => {
    const fromElement = videoRef.current?.duration;
    if (Number.isFinite(fromElement) && (fromElement ?? 0) > 0 && !isRemux) return fromElement!;
    return item.data?.durationSec ?? null;
  }, [item.data?.durationSec, isRemux]);

  /**
   * Resume position, reported every ten seconds and read back on the next device.
   *
   * Disabled for live, which has no position to resume to — the server refuses one
   * anyway, so leaving it on would just produce a 400 every ten seconds.
   */
  const progress = useWatchProgress({
    itemId: id ?? null,
    enabled: Boolean(item.data) && !isLive,
    currentTime,
    durationSec: playbackDuration,
  });

  /**
   * Records the viewing in history.
   *
   * Fires once per item, on the session being created rather than on mount, so opening
   * the page and immediately backing out of a stream that never started does not leave
   * a row. Live is included here even though progress excludes it — "what were we
   * watching last night" is a real question for a channel too.
   */
  const sessionToken = stream.session?.token ?? null;
  useEffect(() => {
    if (!id || !sessionToken) return;
    void api('/api/history', { method: 'POST', body: { itemId: id } }).catch(() => {
      /* history is a convenience; never surface its failure over the film */
    });
  }, [id, sessionToken]);

  // Read through a ref so the engine effect does not take a dependency on the hook's
  // return object, which is a new literal on every render — the same mistake that once
  // made seeking snap back to 0:00.
  const progressResumeRef = useRef(progress.resumeAt);
  progressResumeRef.current = progress.resumeAt;

  /**
   * Flush on pause and after a seek.
   *
   * These are the two moments the position changes by more than the ten-second timer can
   * capture, and they are also when a viewer is most likely to walk away — so a report
   * here is the difference between resuming where they stopped and resuming ten seconds
   * earlier every single time.
   */
  const flushProgress = progress.flush;
  useEffect(() => {
    if (!videoEl) return;
    videoEl.addEventListener('pause', flushProgress);
    videoEl.addEventListener('seeked', flushProgress);
    return () => {
      videoEl.removeEventListener('pause', flushProgress);
      videoEl.removeEventListener('seeked', flushProgress);
    };
  }, [videoEl, flushProgress]);

  /**
   * Seeks a remuxed stream by restarting the conversion at the requested second.
   *
   * `load()` is required: assigning `src` alone leaves Chrome playing the previous
   * buffer, so the picture keeps running from the old position while the new stream
   * arrives behind it.
   */
  const loadRemux = useCallback(
    (seconds: number, offsetMs: number) => {
      const video = videoRef.current;
      const session = stream.session;
      if (!video || !session) return;

      setRemuxOffset(seconds);
      video.src = `${session.playbackUrl}?t=${Math.floor(seconds)}&ao=${Math.round(offsetMs)}`;
      // `load()` is required: assigning `src` alone leaves Chrome playing the previous
      // buffer, so the picture keeps running from the old position while the new stream
      // arrives behind it.
      video.load();
      void video.play().catch(() => {
        /* the user can press play; a refused autoplay is not an error */
      });
    },
    [stream.session],
  );

  const seekRemux = useCallback(
    (seconds: number) => loadRemux(seconds, audioOffsetMs),
    [loadRemux, audioOffsetMs],
  );

  /**
   * Seeking, for whichever engine is attached.
   *
   * Three ways, in the order they are capable of. AVPlay decodes on a plane the element
   * cannot see, so its own `seek` is the only thing that moves the picture; the remux
   * engine is a pipe, so it restarts the conversion upstream; everything else assigns
   * `currentTime` like an ordinary media element.
   *
   * Reads the engine through the ref rather than closing over it, because it is rebuilt
   * whenever a session is re-minted and a captured reference would drive the torn-down one.
   */
  const seekAny = useCallback(
    (seconds: number) => {
      const controls = engineRef.current?.controls;
      if (controls) controls.seek(seconds);
      else if (isRemux) seekRemux(seconds);
      else if (videoRef.current) videoRef.current.currentTime = seconds;
    },
    [isRemux, seekRemux],
  );

  /**
   * Applies a new sync trim without losing the viewer's place.
   *
   * The correction is baked in by ffmpeg, so it can only take effect on a fresh request —
   * which means restarting at the current position rather than from the top. Adjusting by
   * ear would be impossible if every nudge sent you back to the opening titles.
   */
  const changeAudioOffset = useCallback(
    (ms: number) => {
      const clamped = Math.max(-5000, Math.min(5000, ms));
      setAudioOffsetMs(clamped);
      localStorage.setItem(AUDIO_OFFSET_KEY, String(clamped));
      const here = Math.floor((videoRef.current?.currentTime ?? 0) + remuxOffset);
      loadRemux(here, clamped);
    },
    [loadRemux, remuxOffset],
  );

  /**
   * The remote takes over the player on a television.
   *
   * Enabled by device rather than by user agent alone, so `?tv=1` in a desktop browser
   * exercises exactly the same code path — the arrow keys seek there too, which is the
   * only practical way to test this without an `sdb install` between every change.
   */
  const isTv = useDeviceProfile() === 'tv';

  /**
   * The track menu on the television.
   *
   * Only offered when there is something to choose. `tracks` is null for the three engines
   * that cannot enumerate, and a stream with one audio track and no subtitles has nothing
   * to show either — in both cases Up and Down go back to being inert, which is better
   * than a panel that opens over the film to say there is nothing in it.
   */
  const [tvMenuOpen, setTvMenuOpen] = useState(false);
  const tvMenuUsable = (() => {
    if (!isTv || !tracks) return false;
    const set = tracks.get();
    return set.audio.length > 1 || set.subtitle.length > 0;
  })();

  // Back closes the menu before it means anything else. Registered ahead of the back
  // stack's own navigation, which is what `useBackHandler` is for — returning true
  // swallows the press.
  useBackHandler(() => {
    if (!tvMenuOpen) return false;
    setTvMenuOpen(false);
    return true;
  }, isTv);

  const tvKeys = useTvPlayerKeys({
    // Stood down while the menu is up, so the arrow keys reach spatial navigation and can
    // move between its rows. Without this the capture-phase handler would go on seeking
    // the film underneath and the list could never be navigated.
    enabled: isTv && !tvMenuOpen,
    video: videoEl,
    isLive: Boolean(isLive),
    seek: isRemux ? seekRemux : null,
    currentTime,
    durationSec: playbackDuration,
    openMenu: tvMenuUsable ? () => setTvMenuOpen(true) : null,
    // Read through a getter rather than passed by value: the engine is rebuilt on a
    // re-mint, and a captured reference would drive the torn-down one.
    controls: () => engineRef.current?.controls ?? null,
  });

  /*
   * The phone drives the same actions the remote does.
   *
   * Enabled on the television only. A phone controlling the phone it is held in would be
   * the long way round to a button already on the screen, and the server would be relaying
   * a device's commands back to itself.
   */
  const channel = useRealtimeChannel();
  useTvPlayback({
    enabled: isTv,
    itemId: id ?? null,
    actions: tvKeys.actions,
    currentTime,
    // The phone's scrubber needs a number, not "unknown" — it draws a zero-length bar for
    // an unknown length, which is the honest shape for a stream nobody can measure.
    durationSec: useCallback(() => playbackDuration() ?? 0, [playbackDuration]),
    paused: useCallback(() => {
      const controls = engineRef.current?.controls;
      if (controls) return controls.paused();
      return videoRef.current?.paused ?? true;
    }, []),
    // Read from the ref rather than the state copy: a re-mint replaces the engine and the
    // phone's next command must reach the one that is actually playing.
    tracks: useCallback(() => engineRef.current?.tracks ?? null, []),
    send: channel.send,
    onMessage: channel.subscribe,
    onNavigate: useCallback(
      (where: 'back' | 'home') => {
        if (where === 'home') void navigate('/home');
        else void navigate(-1);
      },
      [navigate],
    ),
  });

  // `stream.reportFailure`, not `stream`: the hook returns a fresh object every render, so
  // depending on the whole thing gave this callback a new identity each time — which made
  // the engine effect below re-run on every render and reload the video from the start.
  // On a remuxed stream that was visible as a seek snapping back to 0:00.
  const reportFailure = stream.reportFailure;
  const onEngineError = useCallback(
    (message: string) => {
      setPlayerError(message);
      // Clears the item's learned format server-side, so the next play re-probes rather
      // than repeating whatever just failed.
      reportFailure(message);
    },
    [reportFailure],
  );

  // Build the engine. Keyed on `generation`, never on the session object.
  useEffect(() => {
    const video = videoRef.current;
    const session = stream.session;
    if (!video || !session) return;

    let cancelled = false;
    setPlayerError(null);

    // Two different resume points, in priority order. `stream.resumeAt` is where the
    // viewer was a moment ago when a session was re-minted mid-playback; the stored
    // progress is where they were last time, possibly on another device. The live one
    // wins — restoring a week-old position over the frame that was on screen a second
    // ago would be plainly wrong.
    // `restart` beats both: the viewer just chose "from the beginning" on the previous
    // screen, and honouring a stored position over an explicit request is the kind of
    // thing that makes an app feel broken.
    const resume = restart ? 0 : (stream.resumeAt ?? progressResumeRef.current() ?? 0);
    // A re-minted remux session starts from the top unless it is told otherwise; the
    // resume position is applied through `?t=` because `currentTime` cannot seek a pipe.
    const startAt = session.engine === 'remux' ? Math.floor(resume) : 0;
    if (session.engine === 'remux') setRemuxOffset(startAt);

    // Query parameters belong only on the remux endpoint. Appending them to an HLS
    // playlist or a validated direct_source would change a URL that is not ours to edit.
    const url =
      session.engine === 'remux'
        ? `${session.playbackUrl}?t=${startAt}&ao=${Math.round(audioOffsetMs)}`
        : session.playbackUrl;

    /**
     * The one engine decision the client makes for itself.
     *
     * Whether a container can be demuxed is something only this browser knows — the
     * answer differs between Chrome, Firefox and the panel — so the server cannot decide
     * it. When the browser says no and AVPlay is there, the platform decoder takes over
     * rather than the film failing with a black picture and a warning underneath it.
     */
    const engine =
      session.engine === 'native' && support === 'no' && hasAvplay() ? 'avplay' : session.engine;

    void attachEngine(engine, {
      video,
      url,
      isLive: Boolean(isLive),
      onError: onEngineError,
      // From the profile the viewer chose, so two people in the same house watching the
      // same file get their own language without either of them touching a menu.
      preferredAudioLanguage: profile?.audioLanguage ?? null,
      preferredSubtitleLanguage: profile?.subtitleLanguage ?? null,
    }).then((engine) => {
      if (cancelled) {
        engine.detach();
        return;
      }
      engineRef.current = engine;
      setTracks(engine.tracks ?? null);

      // `remux` already resumed through `?t=` above; assigning currentTime on a pipe
      // would either do nothing or stall the element waiting for a seek it cannot serve.
      if (session.engine !== 'remux' && resume > 0) {
        // Seek back to where the previous session left off. `loadedmetadata` is the
        // first moment the element will accept a seek.
        const restore = () => {
          video.currentTime = resume;
          video.removeEventListener('loadedmetadata', restore);
        };
        video.addEventListener('loadedmetadata', restore);
      }
      void video.play().catch(() => {
        /* autoplay refused — the user presses play; not an error worth showing */
      });
    });

    return () => {
      cancelled = true;
      engineRef.current?.detach();
      engineRef.current = null;
      // Cleared with the engine: the old interface would still answer, driving a player
      // that has been torn down, and the menu would list the previous film's tracks.
      setTracks(null);
    };
    /*
     * `videoEl` is in the list because the element can mount after the session resolves;
     * without it the effect would have run once against null and never retried.
     *
     * Five values are read here and deliberately left out, because re-running this effect
     * means tearing down the engine and reloading the film from scratch:
     *
     *   stream.session, stream.resumeAt   `generation` is the intended trigger. The hook
     *                                     returns a new object every render, so depending
     *                                     on it reloaded the video continuously — the bug
     *                                     that made seeking snap back to 0:00.
     *   audioOffsetMs                     Applied imperatively: the A/V slider calls
     *                                     `loadRemux(here, clamped)` itself, which is what
     *                                     keeps the picture at the current second instead
     *                                     of restarting the film.
     *   profile?.audioLanguage,           Read at attach time. The profile cannot change
     *   profile?.subtitleLanguage         while this screen is mounted, and honouring a
     *                                     change by reloading would be worse than not.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see the note above; each omission is deliberate
  }, [stream.generation, isLive, onEngineError, videoEl, support, restart]);

  // `!progress.loaded` is part of the gate on purpose: attaching the engine before the
  // stored position is known would start the film at 0:00 and then jump.
  if (item.loading || stream.loading || !progress.loaded)
    return <Spinner label="Yayın hazırlanıyor…" />;
  if (item.error) return <ErrorBox message={item.error} onRetry={item.reload} />;

  return (
    <main className="watch">
      {/* On the TV the Back key already does this and there is no pointer to click it
          with; leaving it in would only be one more thing for the focus system to land
          on in front of the picture. */}
      {!isTv && (
        <button type="button" className="watch__back" onClick={() => navigate(-1)}>
          ← Geri
        </button>
      )}

      <div className="watch__heading">
        <h1>{item.data?.title}</h1>
        {item.data && <FavoriteButton item={item.data} />}
        {item.data && <RecommendButton itemId={item.data.id} />}
        {/* Absent on the television, which is already the thing being played on. */}
        {item.data && !isTv && <PlayOnTvButton itemId={item.data.id} />}
      </div>

      {/* The wrapper, not the video, is what goes fullscreen — otherwise our own control
          bar would be left behind on the page while the picture filled the screen. */}
      <div className="watch__stage" ref={setStageEl}>
        {/* The player UI on a television. The native control bar cannot be driven by a
            DPAD, so this replaces it rather than sitting alongside it. */}
        {isTv && tvMenuOpen && (
          <TrackMenu
            tv
            tracks={tracks}
            engine={stream.session?.engine ?? null}
            onClose={() => setTvMenuOpen(false)}
          />
        )}

        {isTv && (
          <TvPlayerBar
            title={item.data?.title ?? ''}
            isLive={Boolean(isLive)}
            video={videoEl}
            controls={() => engineRef.current?.controls ?? null}
            fallbackDurationSec={item.data?.durationSec ?? null}
            activity={tvKeys.activity}
            hint={tvKeys.hint}
            pendingSeek={tvKeys.pendingSeek}
            canOpenMenu={tvMenuUsable}
          />
        )}

        <video
          ref={setVideoEl}
          className="watch__video"
          /*
           * Never the browser's own bar.
           *
           * It used to be shown for the engines whose scrubber worked, which meant the
           * player looked and behaved differently depending on which format an item
           * happened to be in — and Chromium's bar has no audio-track control, so on
           * hls.js, the engine where switching tracks works best, the menu had nowhere to
           * live. Ours replaces it for every engine rather than sitting under it.
           *
           * On the TV it was already off for a second reason: a DPAD cannot reach it, and
           * it covers the bottom of the picture with controls nobody can operate.
           */
          controls={false}
          playsInline
          // Live has no meaningful position to resume, so no poster flash on reload.
          poster={item.data?.poster ?? undefined}
        />

        {/* Pointer-driven, so the television gets `TvPlayerBar` instead — a draggable
            scrubber has no DPAD equivalent, and the remote drives the same callbacks. */}
        {!isTv && (
          <PlayerControls
            video={videoEl}
            container={stageEl}
            durationSec={item.data?.durationSec ?? null}
            offset={remuxOffset}
            onSeek={seekAny}
            seekRestarts={isRemux}
            tracks={tracks}
            engineName={stream.session?.engine ?? null}
            audioOffsetMs={audioOffsetMs}
            // Only the remux path has server-side audio to shift; on every other engine
            // the trim would be a control with nothing behind it.
            onAudioOffset={isRemux ? changeAudioOffset : undefined}
          />
        )}
      </div>

      {/* One line, not a paragraph. The bar already shows what is happening; this only
          has to explain why seeking is slower than usual. */}
      {isRemux && <p className="watch__meta">Ses dönüştürülüyor — ileri/geri alma birkaç saniye sürer.</p>}

      {support !== 'yes' && !isRemux && (
        <p className="watch__warn">
          Bu dosya <code>.{item.data?.containerExt}</code> biçiminde.{' '}
          {support === 'no'
            ? 'Bu tarayıcı bu biçimi çözemiyor — Chrome veya Edge deneyin, ya da VLC gibi bir oynatıcı kullanın.'
            : 'Tarayıcınız bunu oynatabilir, ama içindeki ses/görüntü kodeklerine bağlı.'}
        </p>
      )}

      {stream.error && <ErrorBox message={stream.error} onRetry={stream.retry} />}
      {playerError && <ErrorBox message={playerError} onRetry={stream.retry} />}

      {stream.session && (
        <p className="watch__meta">
          {stream.session.format} · {stream.session.engine}
          {/* Expiry is shown for transparency; renewal happens on its own. */}
          {' · '}
          oturum {new Date(stream.session.expiresAt).toLocaleTimeString('tr')} sonuna kadar
        </p>
      )}
    </main>
  );
}
